import React, { useState, useRef, useEffect } from 'react';
import { useMortgageFlow } from '../context/MortgageFlowContext';
import MessageBubble from './MessageBubble';
import InputArea from './InputArea';
import ProgressBar from './ProgressBar';
import Confetti from './Confetti';
import { getMortgageFlow, getProgressInfo } from '../utils/mortgageFlow';
import { getTranslation } from '../utils/translations';
import { sendApplicationEmail } from '../services/emailService';
import { getClientTestimonials } from '../utils/clientTestimonials';
import './ChatInterface.css';

// Function to parse various date formats and return MM/DD/YYYY
const parseDateInput = (input) => {
  // Remove extra spaces and normalize
  const normalized = input.replace(/\s+/g, ' ').trim();
  
  // Try different date patterns
  const patterns = [
    // MM/DD/YYYY, MM-DD-YYYY, MM.DD.YYYY
    /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
    // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY (European style)
    /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
    // YYYY/MM/DD, YYYY-MM-DD, YYYY.MM.DD
    /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/,
    // Month DD, YYYY or Month DD YYYY
    /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/,
    // DD Month YYYY
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/
  ];
  
  const monthNames = {
    'january': '01', 'jan': '01', 'february': '02', 'feb': '02',
    'march': '03', 'mar': '03', 'april': '04', 'apr': '04',
    'may': '05', 'june': '06', 'jun': '06', 'july': '07', 'jul': '07',
    'august': '08', 'aug': '08', 'september': '09', 'sep': '09', 'sept': '09',
    'october': '10', 'oct': '10', 'november': '11', 'nov': '11',
    'december': '12', 'dec': '12'
  };
  
  // Try MM/DD/YYYY pattern first
  let match = normalized.match(patterns[0]);
  if (match) {
    const month = match[1].padStart(2, '0');
    const day = match[2].padStart(2, '0');
    const year = match[3];
    if (isValidDate(month, day, year)) {
      return `${month}/${day}/${year}`;
    }
  }
  
  // Try YYYY/MM/DD pattern
  match = normalized.match(patterns[2]);
  if (match) {
    const year = match[1];
    const month = match[2].padStart(2, '0');
    const day = match[3].padStart(2, '0');
    if (isValidDate(month, day, year)) {
      return `${month}/${day}/${year}`;
    }
  }
  
  // Try Month DD, YYYY pattern
  match = normalized.match(patterns[3]);
  if (match) {
    const monthName = match[1].toLowerCase();
    const day = match[2].padStart(2, '0');
    const year = match[3];
    const month = monthNames[monthName];
    if (month && isValidDate(month, day, year)) {
      return `${month}/${day}/${year}`;
    }
  }
  
  // Try DD Month YYYY pattern
  match = normalized.match(patterns[4]);
  if (match) {
    const day = match[1].padStart(2, '0');
    const monthName = match[2].toLowerCase();
    const year = match[3];
    const month = monthNames[monthName];
    if (month && isValidDate(month, day, year)) {
      return `${month}/${day}/${year}`;
    }
  }
  
  return null;
};

const isValidDate = (month, day, year) => {
  const m = parseInt(month);
  const d = parseInt(day);
  const y = parseInt(year);
  
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  if (y < 1900 || y > new Date().getFullYear()) return false;
  
  // Check for valid day in month
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) {
    daysInMonth[1] = 29; // Leap year
  }
  
  return d <= daysInMonth[m - 1];
};

const ChatInterface = ({ language, onLanguageChange, showClients, showLoanApp }) => {
  const { messages, addMessage, updateMessage, currentStep, userData, updateUserData, setStep } = useMortgageFlow();
  // const [isTyping, setIsTyping] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const hasInitialized = useRef(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    // Use requestAnimationFrame for immediate scroll after DOM update
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const chatInterface = document.querySelector('.chat-interface');
        if (chatInterface) {
          chatInterface.scrollTop = chatInterface.scrollHeight;
        }
      });
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with welcome message only once
    if (!hasInitialized.current && messages.length === 0) {
      hasInitialized.current = true;
      const welcomeMessage = {
        id: Date.now(),
        text: getTranslation('languageChoice', language),
        sender: 'bot',
        timestamp: new Date()
      };
      addMessage(welcomeMessage);
    }
  }, [messages.length, addMessage, language]);

  // Update all bot messages when language changes
  useEffect(() => {
    if (hasInitialized.current && messages.length > 0) {
      const flow = getMortgageFlow();
      
      // Update welcome message (first message)
      const firstMessage = messages[0];
      if (firstMessage && firstMessage.sender === 'bot') {
        updateMessage(firstMessage.id, {
          text: getTranslation('languageChoice', language)
        });
      }
      
      // Update all other bot messages based on current step (but skip error messages)
      messages.forEach((message, index) => {
        if (message.sender === 'bot' && index > 0 && !message.text.includes('valid email')) {
          // Find the corresponding step for this message
          const stepKeys = Object.keys(flow);
          const stepIndex = Math.floor(index / 2); // Every 2nd message is roughly a new step
          
          if (stepKeys[stepIndex]) {
            const stepKey = stepKeys[stepIndex];
            const translatedText = getTranslation(stepKey, language);
            if (translatedText && translatedText !== stepKey) {
              updateMessage(message.id, {
                text: translatedText
              });
            }
          }
        }
      });
    }
  }, [language]);

  const handleUserMessage = async (message) => {
    // Add user message
    const userMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };
    addMessage(userMessage);

    // Process message and get bot response immediately
    const flow = getMortgageFlow();
    const currentStepData = flow[currentStep];
    
    if (currentStepData) {
      // Handle language selection
      if (currentStepData.field === 'languageChoice') {
        const userChoice = message.trim().toLowerCase();
        let selectedLanguage = 'en'; // default
        
        if (userChoice.includes('kyrgyz') || userChoice.includes('кыргыз')) {
          selectedLanguage = 'ky';
        } else if (userChoice.includes('russian') || userChoice.includes('русский')) {
          selectedLanguage = 'ru';
        } else if (userChoice.includes('english')) {
          selectedLanguage = 'en';
        }
        
        // Update the language in parent component
        if (onLanguageChange) {
          onLanguageChange(selectedLanguage);
        }
      }
      
      // Validate email if current step has email field
      if (currentStepData.field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = emailRegex.test(message.trim());
        if (!isValid) {
          const errorMessage = {
            id: Date.now() + 1,
            text: getTranslation('emailError', language) || "Please enter a valid email address (example: user@domain.com)",
            sender: 'bot',
            timestamp: new Date()
          };
          addMessage(errorMessage);
          
          // Force scroll after error message
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const chatInterface = document.querySelector('.chat-interface');
              if (chatInterface) {
                chatInterface.scrollTop = chatInterface.scrollHeight;
              }
            });
          });
          return; // Don't proceed to next step
        }
      }
      
      // Validate phone number if current step has phone field
      if (currentStepData.field === 'phone') {
        // Remove all non-digit characters
        const digitsOnly = message.trim().replace(/\D/g, '');
        const isValid = digitsOnly.length === 10;
        if (!isValid) {
          const errorMessage = {
            id: Date.now() + 1,
            text: getTranslation('phoneError', language) || "Please enter a valid 10-digit phone number (example: 555-123-4567)",
            sender: 'bot',
            timestamp: new Date()
          };
          addMessage(errorMessage);
          
          // Force scroll after error message
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const chatInterface = document.querySelector('.chat-interface');
              if (chatInterface) {
                chatInterface.scrollTop = chatInterface.scrollHeight;
              }
            });
          });
          return; // Don't proceed to next step
        }
      }
      
      // Parse and validate date of birth
      if (currentStepData.field === 'dateOfBirth') {
        const parsedDate = parseDateInput(message.trim());
        if (!parsedDate) {
          const errorMessage = {
            id: Date.now() + 1,
            text: getTranslation('dateError', language) || "Please enter a valid date (examples: 01/15/1990, January 15 1990, 15-01-1990)",
            sender: 'bot',
            timestamp: new Date()
          };
          addMessage(errorMessage);
          
          // Force scroll after error message
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const chatInterface = document.querySelector('.chat-interface');
              if (chatInterface) {
                chatInterface.scrollTop = chatInterface.scrollHeight;
              }
            });
          });
          return; // Don't proceed to next step
        }
        // Update the message to be the formatted date
        message = parsedDate;
      }
      
      // Validate credit score if current step has credit score field
      if (currentStepData.field === 'creditScore') {
        const creditScore = message.trim().toLowerCase();
        // Allow "unknown" or numeric values
        if (creditScore !== 'unknown' && creditScore !== 'белгисиз' && creditScore !== 'desconocido' && creditScore !== 'неизвестно') {
          const score = parseInt(creditScore);
          // FICO scores range from 300-850
          if (isNaN(score) || score < 300 || score > 850) {
            const errorMessage = {
              id: Date.now() + 1,
              text: getTranslation('creditScoreError', language) || "Please enter a valid credit score between 300-850, or type 'Unknown' if you don't know your score.",
              sender: 'bot',
              timestamp: new Date()
            };
            addMessage(errorMessage);
            
            // Force scroll after error message
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const chatInterface = document.querySelector('.chat-interface');
                if (chatInterface) {
                  chatInterface.scrollTop = chatInterface.scrollHeight;
                }
              });
            });
            return; // Don't proceed to next step
          }
        }
      }

      // Update user data based on current step
      console.log('Continuing with normal flow after validation');
      const fieldName = currentStepData.field;
      if (fieldName) {
        updateUserData({ [fieldName]: message });
      }

      // Get next step
      const nextStep = currentStepData.next;
      
      if (nextStep && flow[nextStep]) {
        setStep(nextStep);
        
        // Check if this is the completion step
        if (nextStep === 'complete') {
          setShowConfetti(true);
          
          // Send application email
          sendApplicationEmail(userData, language).then(result => {
            if (result.success) {
              console.log('Application email sent successfully');
            } else {
              console.error('Failed to send application email:', result.error);
            }
          });

          // Stop confetti after 6 seconds
          setTimeout(() => {
            setShowConfetti(false);
          }, 6000);
        }
        
        // Get translated question for the next step
        const translatedQuestion = getTranslation(nextStep, language);
        
        const botResponse = {
          id: Date.now() + 1,
          text: translatedQuestion,
          sender: 'bot',
          timestamp: new Date()
        };
        addMessage(botResponse);
        
        // Force immediate scroll for every message
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const chatInterface = document.querySelector('.chat-interface');
            if (chatInterface) {
              chatInterface.scrollTop = chatInterface.scrollHeight;
            }
          });
        });
      } else {
        // Application complete - trigger confetti and send email!
        setShowConfetti(true);
        
        // Send application email
        sendApplicationEmail(userData, language).then(result => {
          if (result.success) {
            console.log('Application email sent successfully');
          } else {
            console.error('Failed to send application email:', result.error);
          }
        });
        
        const completionMessage = {
          id: Date.now() + 1,
          text: getTranslation('complete', language) || "Congratulations! 🎉 You've successfully completed your mortgage application! I have collected all the necessary information, and your mortgage consultant will review your application and contact you within 24-48 hours with next steps. Thank you for choosing us for your home financing needs - we're excited to help you achieve your homeownership dreams!",
          sender: 'bot',
          timestamp: new Date()
        };
        addMessage(completionMessage);
        
        // Force immediate scroll for completion message
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const chatInterface = document.querySelector('.chat-interface');
            if (chatInterface) {
              chatInterface.scrollTop = chatInterface.scrollHeight;
            }
          });
        });

        // Stop confetti after 6 seconds
        setTimeout(() => {
          setShowConfetti(false);
        }, 6000);
      }
    }
  };

  const progressInfo = getProgressInfo(currentStep);

  return (
    <div className="chat-interface">
      <Confetti isActive={showConfetti} />
      {showLoanApp && (
        <ProgressBar 
          currentStep={progressInfo.currentStep}
          totalSteps={progressInfo.totalSteps}
          percentage={progressInfo.percentage}
          language={language}
        />
      )}
      <div className="messages-container">
        {showLoanApp && messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {showClients && (
          <div className="client-testimonials">
            <div className="testimonials-header">
              <h3>{getTranslation('happyClients', language)}</h3>
              <p>{getTranslation('clientsDescription', language)}</p>
            </div>
            <div className="testimonials-grid">
              {getClientTestimonials(language).map((client) => (
                <div key={client.id} className="testimonial-card">
                  {client.photo && (
                    <div className="testimonial-photo">
                      <img src={client.photo} alt={client.name} />
                    </div>
                  )}
                  <div className="testimonial-content">
                    <p>"{client.message}"</p>
                  </div>
                  <div className="testimonial-footer">
                    <div className="client-info">
                      <strong>{client.name}</strong>
                      <small>{client.location}</small>
                    </div>
                    <div className="loan-info">
                      <span className="loan-amount">{client.loanAmount}</span>
                      <span className="loan-type">{client.loanType}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* {isTyping && (
          <div className="typing-indicator">
            <div className="typing-bubble">
              <div className="typing-avatar">🤖</div>
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )} */}
        <div ref={messagesEndRef} />
        {showLoanApp && (
          <div style={{ maxWidth: '48rem', width: '100%', padding: '0 1rem', marginTop: '0' }}>
            <InputArea onSendMessage={handleUserMessage} disabled={false} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;